import { LegalLayout, LegalSection } from "@/components/LegalLayout";

function Bullets({ items }) {
  return (
    <ul className="list-disc pl-5 space-y-1.5">
      {items.map((i) => <li key={i}>{i}</li>)}
    </ul>
  );
}

const SUBPROCESSORS = ["OpenAI", "Emergent", "MongoDB", "Stripe", "Resend", "Mailchimp", "Google Analytics 4", "Microsoft Clarity"];

export default function SecurityPage() {
  return (
    <LegalLayout
      eyebrow="Sikkerhed & databehandling"
      title="Sikkerhed & databehandling"
      intro="Tillid er afgørende, når virksomheder vælger en AI-platform. Hos Aicom AI ved vi, at vores kunder deler fortrolige oplysninger med os. Derfor arbejder vi målrettet med datasikkerhed, GDPR og ansvarlig anvendelse af kunstig intelligens."
    >
        <p className="text-muted-foreground leading-relaxed">Vores mål er enkelt: I skal føle jer trygge ved at bruge Aicom AI som en naturlig del af jeres kommunikation.</p>

        <LegalSection title="Hvilke data behandler vi?">
          <p>Aicom AI behandler kun de oplysninger, som I aktivt vælger at dele med platformen. Det kan blandt andet være:</p>
          <Bullets items={[
            "Information om virksomheden",
            "Hjemmeside og URL'er",
            "LinkedIn-profiler",
            "Dokumenter, PDF'er og præsentationer",
            "Cases, pressemeddelelser og nyhedsbreve",
            "Brandfortællinger og kommunikationsstrategier",
            "Logoer og billeder",
            "Øvrigt materiale, som I ønsker analyseret eller videreudviklet",
          ]} />
          <p>Vi indsamler ikke flere oplysninger end nødvendigt for at levere de funktioner, I vælger at benytte.</p>
        </LegalSection>

        <LegalSection title="Hvordan bruger vi oplysningerne?">
          <p>De oplysninger, I deler med Aicom AI, anvendes udelukkende til at levere de funktioner, I benytter. Det kan eksempelvis være:</p>
          <Bullets items={[
            "kommunikationsanalyser",
            "brandfortællinger",
            "LinkedIn-opslag",
            "nyhedsbreve",
            "pressemeddelelser",
            "kampagner",
            "SEO-analyser",
            "AI-genererede forslag",
            "virksomhedsspecifik rådgivning",
          ]} />
          <p className="text-foreground">Vi sælger aldrig jeres data. Vi deler aldrig jeres data med andre kunder.</p>
        </LegalSection>

        <LegalSection title="AI er et værktøj - ikke ejeren af jeres data">
          <p>Aicom AI anvender kunstig intelligens til at analysere og generere indhold. AI anvendes alene til at udføre de opgaver, I beder platformen om.</p>
          <p>Det indhold og de oplysninger, I deler med Aicom AI, tilhører fortsat jer. Vi bruger ikke jeres materiale til markedsføring, videresalg eller til at opbygge andre kunders løsninger.</p>
          <p>Det indhold, I indtaster eller uploader, behandles af vores AI-leverandør OpenAI for at levere analyser og AI-genereret indhold. OpenAI anvender ikke jeres data til at træne deres modeller, men kan opbevare dem i op til 30 dage af hensyn til driftssikkerhed og misbrugskontrol.</p>
        </LegalSection>

        <LegalSection title="Datasikkerhed">
          <p>Vi arbejder løbende med både tekniske og organisatoriske sikkerhedsforanstaltninger. Det omfatter blandt andet:</p>
          <Bullets items={[
            "Krypterede forbindelser (HTTPS/TLS)",
            "Sikker cloud-infrastruktur",
            "Adgangsstyring",
            "Rollebaserede brugerrettigheder",
            "Regelmæssig krypteret backup via vores hostingleverandør",
            "Overvågning af platformen",
            "Softwareopdateringer",
            "Begrænset adgang til kundedata",
          ]} />
          <p>Kun medarbejdere med et arbejdsbetinget behov har adgang til kundedata.</p>
          <p>Vores underliggende hostinginfrastruktur er ISO 27001- (2013 og 2022) og SOC 2 (SSAE 18)-certificeret. Databasen kører på MongoDB Atlas med automatisk krypterede backups.</p>
        </LegalSection>

        <LegalSection title="GDPR">
          <p>Vi behandler personoplysninger i overensstemmelse med databeskyttelsesforordningen (GDPR).</p>
          <p>Hvis I anvender Aicom AI som virksomhed, fungerer I som dataansvarlig for de oplysninger, I uploader, mens PointTaken (som ejer Aicom AI) fungerer som databehandler.</p>
          <p>Vi tilbyder en databehandleraftale (DPA), som beskriver vores ansvar og behandling af data.</p>
        </LegalSection>

        <LegalSection title="Følsomme personoplysninger">
          <p>Aicom AI er ikke beregnet til behandling af følsomme personoplysninger (særlige kategorier af personoplysninger, jf. GDPR art. 9 og 10 – fx helbred, CPR-numre, race, religion, politisk overbevisning eller strafbare forhold). Du må ikke uploade eller indtaste sådanne oplysninger i tjenesten.</p>
          <p>Gør du det alligevel, sker det udelukkende på dit eget ansvar og risiko. Du indestår i så fald for at have det nødvendige lovlige grundlag, og PointTaken påtager sig intet ansvar for følsomme personoplysninger, som du i strid med dette uploader.</p>
        </LegalSection>

        <LegalSection title="Underdatabehandlere">
          <p>For at kunne levere Aicom AI samarbejder vi med en række nøje udvalgte teknologileverandører. Vi anvender blandt andet:</p>
          <Bullets items={SUBPROCESSORS} />
          <p>Alle leverandører er udvalgt med fokus på sikkerhed, stabil drift og overholdelse af gældende databeskyttelsesregler.</p>
        </LegalSection>

        <LegalSection title="Internationale dataoverførsler">
          <p>Visse leverandører behandler oplysninger uden for EU/EØS (bl.a. i USA og Indien), herunder vores hosting-/infrastrukturleverandør Emergent samt OpenAI, Stripe, Google (Analytics) og Microsoft (Clarity). Overførslen sker på et lovligt grundlag: Emergent har indgået en databehandleraftale med os, der inkorporerer EU-Kommissionens standardkontraktbestemmelser (SCC). Google og Microsoft er certificeret under EU-U.S. Data Privacy Framework.</p>
        </LegalSection>

        <LegalSection title="Ejerskab over jeres data">
          <p>Jeres data er jeres data. Alt materiale, som I uploader eller producerer gennem Aicom AI, tilhører fortsat jer. Det gælder eksempelvis:</p>
          <Bullets items={[
            "dokumenter",
            "analyser",
            "brandfortællinger",
            "kampagner",
            "pressemeddelelser",
            "nyhedsbreve",
            "LinkedIn-opslag",
            "AI-genereret indhold",
          ]} />
        </LegalSection>

        <LegalSection title="Når samarbejdet ophører">
          <p>Hvis I vælger at opsige jeres abonnement, behandles jeres data i overensstemmelse med vores handelsbetingelser og databehandleraftale.</p>
          <p>Personoplysninger og virksomhedsmateriale slettes eller anonymiseres, når der ikke længere er et sagligt eller lovmæssigt grundlag for at opbevare dem. Vores hostingleverandør sletter eller anonymiserer data senest 45 dage efter kontoens ophør. Data i rutinemæssige backups isoleres og slettes i henhold til hostingleverandørens backup-cyklus.</p>
        </LegalSection>

        <LegalSection title="Har du spørgsmål?">
          <p>Vi ved, at datasikkerhed er afgørende. Har du spørgsmål om vores behandling af data, GDPR eller sikkerheden i Aicom AI, er du altid velkommen til at kontakte os.</p>
          <p className="text-foreground">
            PointTaken I/S<br />
            E-mail: <a href="mailto:kontakt@pointtaken.dk" className="underline underline-offset-4 hover:no-underline">kontakt@pointtaken.dk</a>
          </p>
          <p>Du kan til enhver tid hente dine uploadede dokumenter direkte i Vidensbanken. Ønsker du at få din øvrige kontodata udleveret eller din konto slettet, kan du skrive til kontakt@pointtaken.dk.</p>
        </LegalSection>
    </LegalLayout>
  );
}
